import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

/* ─── Types ─── */

export interface CartItem {
  productSlug: string;
  name: string;
  price: number;
  quantity: number;
  selectedVariations: Record<string, string>;
  categorySlug: string;
}

interface CartContextValue {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (productSlug: string, selectedVariations: Record<string, string>) => void;
  updateQuantity: (productSlug: string, selectedVariations: Record<string, string>, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/* ─── Helpers ─── */

const STORAGE_KEY = "zap_store_cart";

function variationKey(variations: Record<string, string>): string {
  return Object.entries(variations).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => `${k}:${v}`).join("|");
}

function itemKey(slug: string, variations: Record<string, string>): string {
  return `${slug}__${variationKey(variations)}`;
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return [];
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

/* ─── Provider ─── */

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Persist to localStorage on change
  useEffect(() => {
    saveCart(items);
  }, [items]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const addItem = useCallback((item: Omit<CartItem, "quantity">, quantity = 1) => {
    setItems((prev) => {
      const key = itemKey(item.productSlug, item.selectedVariations);
      const existing = prev.find((i) => itemKey(i.productSlug, i.selectedVariations) === key);
      if (existing) {
        return prev.map((i) =>
          itemKey(i.productSlug, i.selectedVariations) === key
            ? { ...i, quantity: i.quantity + quantity }
            : i,
        );
      }
      return [...prev, { ...item, quantity }];
    });
    setIsCartOpen(true);
  }, []);

  const removeItem = useCallback((productSlug: string, selectedVariations: Record<string, string>) => {
    const key = itemKey(productSlug, selectedVariations);
    setItems((prev) => prev.filter((i) => itemKey(i.productSlug, i.selectedVariations) !== key));
  }, []);

  const updateQuantity = useCallback((productSlug: string, selectedVariations: Record<string, string>, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productSlug, selectedVariations);
      return;
    }
    const key = itemKey(productSlug, selectedVariations);
    setItems((prev) =>
      prev.map((i) =>
        itemKey(i.productSlug, i.selectedVariations) === key
          ? { ...i, quantity }
          : i,
      ),
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen((p) => !p), []);

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalPrice,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

export function useCartSafe(): CartContextValue | null {
  return useContext(CartContext);
}
